import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
    _ytApiLoaded: boolean;
    _ytApiCallbacks: (() => void)[];
  }
}

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(); return; }
    if (!window._ytApiCallbacks) window._ytApiCallbacks = [];
    window._ytApiCallbacks.push(resolve);
    if (!window._ytApiLoaded) {
      window._ytApiLoaded = true;
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prevReady) prevReady();
        (window._ytApiCallbacks || []).forEach((cb) => cb());
        window._ytApiCallbacks = [];
      };
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  });
}

interface YouTubeEmbedProps { videoId: string; title: string; }

function YouTubeEmbed({ videoId, title }: YouTubeEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  useEffect(() => {
    if (!isPlaying) {
      if (playerRef.current) {
        try { playerRef.current.stopVideo(); } catch { /* ignore */ }
        try { playerRef.current.destroy(); } catch { /* ignore */ }
        playerRef.current = null;
      }
      if (playerContainerRef.current) {
        while (playerContainerRef.current.firstChild)
          playerContainerRef.current.removeChild(playerContainerRef.current.firstChild);
      }
      return;
    }
    loadYouTubeAPI().then(() => {
      if (!playerContainerRef.current) return;
      const div = document.createElement('div');
      playerContainerRef.current.appendChild(div);
      playerRef.current = new window.YT.Player(div, {
        videoId, width: '100%', height: '100%',
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (event: any) => {
            if (event.data === window.YT.PlayerState.ENDED) setIsPlaying(false);
          },
        },
      });
    });
    return () => {
      if (playerRef.current) { try { playerRef.current.destroy(); } catch { /* ignore */ } playerRef.current = null; }
    };
  }, [isPlaying, videoId]);

  return (
    <div className="w-full h-full relative">
      <div
        className={`absolute inset-0 cursor-pointer group transition-opacity duration-300 ${isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        onClick={() => setIsPlaying(true)}
      >
        <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`; }} />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
          <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-red-600 rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300">
            <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
          </div>
        </div>
      </div>
      <div ref={playerContainerRef}
        className={`absolute inset-0 transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />
    </div>
  );
}

export default function Reference() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const referenceCards = [
    { icon: 'ri-file-text-line', titleKey: 'ref_card0_title', descKey: 'ref_card0_desc' },
    { icon: 'ri-book-line', titleKey: 'ref_card1_title', descKey: 'ref_card1_desc' },
    { icon: 'ri-award-line', titleKey: 'ref_card2_title', descKey: 'ref_card2_desc' },
    { icon: 'ri-flask-line', titleKey: 'ref_card3_title', descKey: 'ref_card3_desc' },
  ];

  const videoItems = [
    { id: 'ETffKL0M1tw', labelKey: 'ref_video0' },
    { id: 'TYTfmtwozvM', labelKey: 'ref_video1' },
    { id: 'EtmV5dspsNs', labelKey: 'ref_video2' },
  ];

  const articles = [
    { titleKey: 'ref_article0_title', source: 'https://health.chosun.com', url: 'https://health.chosun.com/site/data/html_dir/2024/01/05/2024010500775.html', icon: 'ri-article-line' },
    { titleKey: 'ref_article1_title', source: 'https://www.sciencedirect.com/science/article/abs/pii/S030881461530296X', url: 'https://www.sciencedirect.com/science/article/abs/pii/S030881461530296X', icon: 'ri-article-line' },
    { titleKey: 'ref_article2_title', source: 'https://www.sciencedirect.com/science/article/abs/pii/088915759290045L', url: 'https://www.sciencedirect.com/science/article/abs/pii/088915759290045L', icon: 'ri-article-line' },
  ];

  const certImages = [
    { src: 'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/80325915e9a4460acddf0ac12e5f4d5e.jpeg', alt: 'Certificate 1' },
    { src: 'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/f4e6de22f82e8b093a2d9ff1549f01cd.jpeg', alt: 'Certificate 2' },
    { src: 'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/789a5ab5d56aa387c476edbb5b1c6d41.jpeg', alt: 'Certificate 3' },
    { src: 'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/6472ffaa1da74441b907e9d1da73efd2.jpeg', alt: 'Certificate 4' },
    { src: 'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/7e7270f5384579a4eea50d73c2dc4ac9.jpeg', alt: 'Certificate 5' },
  ];

  const handlePrev = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + certImages.length) % certImages.length);
  };
  const handleNext = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % certImages.length);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setLightboxIndex(null);
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'ArrowRight') handleNext();
  };

  return (
    <section id="reference" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
            {t('ref_title')}
          </h2>
          <p className="text-base md:text-xl text-gray-600" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
            {t('ref_sub')}
          </p>
        </div>

        {/* Reference cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {referenceCards.map((ref, index) => (
            <button key={index} className="bg-gray-50 rounded-lg p-5 md:p-8 text-center hover:bg-teal-50 hover:shadow-lg transition-all duration-300 cursor-pointer group">
              <div className="w-14 h-14 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-4 md:mb-6 bg-white rounded-full shadow-md group-hover:bg-teal-500 transition-colors duration-300">
                <i className={`${ref.icon} text-2xl md:text-4xl text-teal-500 group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                {t(ref.titleKey)}
              </h3>
              <p className="text-xs md:text-sm text-gray-600" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                {t(ref.descKey)}
              </p>
            </button>
          ))}
        </div>

        {/* Video section */}
        <div className="mt-14 md:mt-20">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
              {t('ref_video_title')}
            </h3>
            <div className="w-12 md:w-16 h-1 bg-teal-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-5 md:gap-8">
            {videoItems.map((video, index) => (
              <div key={index} className="flex flex-col gap-2 md:gap-3">
                <div className="w-full aspect-video rounded-xl overflow-hidden shadow-md">
                  <YouTubeEmbed videoId={video.id} title={t(video.labelKey)} />
                </div>
                <p className="text-xs md:text-base font-semibold text-gray-800 text-center px-2" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                  {t(video.labelKey)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Patent images */}
        <div className="mt-14 md:mt-20">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
              {t('ref_patent_title')}
            </h3>
            <div className="w-12 md:w-16 h-1 bg-teal-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/97163a6cf52c0d30f747d4cc4f2bafe2.jpeg',
              'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/fe416b717846c705232057ad0b930b7c.jpeg',
              'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/a338283152d6c33ee282bbe1788219c2.jpeg',
              'https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/8743ec1ae211424e0fa9444134a8cfc0.jpeg',
            ].map((src, i) => (
              <div key={i} className="w-full h-40 md:h-64 rounded-xl overflow-hidden shadow-md relative">
                <img src={src} alt={`Document ${i + 1}`} className="w-full h-full object-cover object-top" />
                {i === 0 && (
                  <div
                    className="absolute left-0 right-0"
                    style={{
                      top: '26%',
                      height: '10%',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Cert images */}
        <div className="mt-14 md:mt-20">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
              {t('ref_cert_title')}
            </h3>
            <div className="w-12 md:w-16 h-1 bg-teal-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {certImages.map((img, index) => (
              <div
                key={index}
                className="w-full h-40 md:h-56 rounded-xl overflow-hidden shadow-md cursor-pointer group relative"
                onClick={() => setLightboxIndex(index)}
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-gray-800 text-base md:text-lg"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={() => setLightboxIndex(null)} onKeyDown={handleKeyDown} tabIndex={0}>
            <button className="absolute top-4 right-4 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors duration-200 cursor-pointer z-10"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}>
              <i className="ri-close-line text-xl md:text-2xl"></i>
            </button>
            <button className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors duration-200 cursor-pointer z-10"
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}>
              <i className="ri-arrow-left-s-line text-2xl md:text-3xl"></i>
            </button>
            <div className="max-w-4xl max-h-[85vh] mx-14 md:mx-16 flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}>
              <img src={certImages[lightboxIndex].src} alt={certImages[lightboxIndex].alt}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            </div>
            <button className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors duration-200 cursor-pointer z-10"
              onClick={(e) => { e.stopPropagation(); handleNext(); }}>
              <i className="ri-arrow-right-s-line text-2xl md:text-3xl"></i>
            </button>
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
              {certImages.map((_, i) => (
                <button key={i}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 cursor-pointer ${i === lightboxIndex ? 'bg-white' : 'bg-white/40'}`}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }} />
              ))}
            </div>
          </div>
        )}

        {/* Articles */}
        <div className="mt-14 md:mt-20">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
              {t('ref_articles_title')}
            </h3>
            <div className="w-12 md:w-16 h-1 bg-teal-500 mx-auto rounded-full"></div>
          </div>
          <div className="flex flex-col gap-3 md:gap-4">
            {articles.map((article, index) => (
              <a key={index} href={article.url} target="_blank" rel="nofollow noopener noreferrer"
                className="flex items-center gap-3 md:gap-5 bg-gray-50 rounded-xl px-4 md:px-6 py-4 md:py-5 hover:bg-teal-50 hover:shadow-md transition-all duration-300 group cursor-pointer">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0 bg-white rounded-full shadow-sm group-hover:bg-teal-500 transition-colors duration-300">
                  <i className={`${article.icon} text-lg md:text-2xl text-teal-500 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-base font-semibold text-gray-900 group-hover:text-teal-700 transition-colors duration-300 line-clamp-2 md:truncate" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                    {t(article.titleKey)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 hidden md:block" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
                    {article.source}
                  </p>
                </div>
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0 text-gray-400 group-hover:text-teal-500 transition-colors duration-300">
                  <i className="ri-external-link-line text-base md:text-xl"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
